import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { noteImageUrl } from './thumbs.js';
import { IconCheckbox } from './ui/icons.js';

/**
 * **Le rendu Markdown du site** — et le choix qui compte est celui de la sortie.
 *
 * Rien ici ne produit de HTML. Le rendu va **directement vers des nœuds Preact**,
 * ce qui rend l'injection structurellement impossible : une note contenant
 * `<script>` ou `<img onerror=…>` produit du **texte**, parce qu'aucun chemin de
 * ce fichier ne fabrique d'élément à partir d'une chaîne. La CSP du site —
 * `default-src 'none'` — est la seconde ligne, pas la première ; s'en remettre à
 * elle voudrait dire qu'on accepte de dépendre d'un en-tête pour rester sûr, et
 * une note est du contenu qu'on peut **recevoir d'un groupe**.
 *
 * Le sous-ensemble est **exactement** celui de `vision.md` §2.4, le même que
 * l'éditeur mobile sait écrire : titres, paragraphes, listes, todo, citations,
 * code, séparateurs, tables, et en ligne gras / italique / barré / code / liens /
 * images. Les callouts, couleurs et toggles imbriqués en sortent par décision, et
 * ce qui n'est pas reconnu **reste du texte** — jamais avalé.
 */

// ── Le niveau ligne ──────────────────────────────────────────────────────────

export type Block =
  | { readonly kind: 'heading'; readonly level: number; readonly text: string }
  | { readonly kind: 'para'; readonly text: string }
  | { readonly kind: 'code'; readonly language: string; readonly text: string }
  | { readonly kind: 'quote'; readonly text: string }
  | { readonly kind: 'rule' }
  | { readonly kind: 'list'; readonly ordered: boolean; readonly items: readonly ListItem[] }
  | { readonly kind: 'table'; readonly head: readonly string[]; readonly rows: readonly (readonly string[])[] };

export interface ListItem {
  readonly text: string;
  /** `undefined` = puce ordinaire ; sinon une case, cochée ou non. */
  readonly checked?: boolean;
  /**
   * **Ce qui pend sous cette ligne** — une sous-liste, et rien d'autre.
   *
   * Ajouté avec l'incrément N1 *(2026-08-17)* : jusque-là `BULLET` ignorait le
   * retrait (`^\s*`), donc `- courses` puis `  - pain` produisaient **deux items
   * frères**. L'éditeur, lui, sait imbriquer — le lecteur aurait aplati ce qu'il
   * écrit, et c'est exactement le désaccord de grammaire que le test d'accord
   * (E3) est là pour attraper.
   */
  readonly children?: readonly Block[];
}

const RULE = /^(?:-{3,}|\*{3,}|_{3,})$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const FENCE = /^```\s*([\w+-]*)\s*$/;
/**
 * Une ligne de liste, **son retrait compris** : c'est lui qui porte
 * l'imbrication. Le marqueur est capturé pour distinguer une puce d'un numéro,
 * et non pour être rendu.
 *
 * Le texte est **facultatif** : une ligne qu'on vient d'ouvrir n'en a pas
 * encore, et l'exiger la faisait retomber en paragraphe à la relecture — la
 * ligne disparaissait de la liste sous les yeux de qui venait de l'ouvrir.
 */
const ITEM = /^([ \t]*)(?:([-*+])|(?:\d+[.)]))(?:[ \t]+(.*))?$/;
/**
 * Une case, **avec ou sans texte derrière**. L'espace était exigé jusqu'à N1
 * *(2026-08-17)* : une ligne à cocher qu'on ouvre sans la remplir s'écrit
 * `- [ ]`, et le lecteur en faisait une puce dont le texte était `[ ]`.
 */
const TODO = /^\[([ xX])\](?:\s+(.*))?$/;
const QUOTE = /^>\s?(.*)$/;
const TABLE_SEP = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/;

const cells = (line: string): string[] =>
  line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((c) => c.trim());

export function parseBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Le bloc de code **en premier** : à l'intérieur, plus aucune règle ne joue,
    // sans quoi un `# commentaire` deviendrait un titre au milieu du code.
    const fence = FENCE.exec(line.trim());
    if (fence) {
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^```/.test((lines[i] ?? '').trim())) {
        body.push(lines[i] ?? '');
        i += 1;
      }
      i += 1; // la clôture, présente ou non — un bloc non fermé va jusqu'au bout
      blocks.push({ kind: 'code', language: fence[1] ?? '', text: body.join('\n') });
      continue;
    }

    if (RULE.test(line.trim())) {
      blocks.push({ kind: 'rule' });
      i += 1;
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1]?.length ?? 1, text: heading[2] ?? '' });
      i += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i] ?? '')) {
        body.push(QUOTE.exec(lines[i] ?? '')?.[1] ?? '');
        i += 1;
      }
      blocks.push({ kind: 'quote', text: body.join('\n') });
      continue;
    }

    // Une table demande **deux** lignes pour exister : l'en-tête et le séparateur.
    // Sans cette exigence, un simple `| a | b |` isolé deviendrait un tableau à
    // une ligne, et un texte contenant des barres verticales serait mutilé.
    if (line.includes('|') && TABLE_SEP.test(lines[i + 1] ?? '')) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && (lines[i] ?? '').includes('|') && (lines[i] ?? '').trim() !== '') {
        rows.push(cells(lines[i] ?? ''));
        i += 1;
      }
      blocks.push({ kind: 'table', head, rows });
      continue;
    }

    if (ITEM.test(line)) {
      const list = parseList(lines, i);
      blocks.push(list.block);
      i = list.next;
      continue;
    }

    // Un paragraphe court jusqu'à la ligne vide ou jusqu'à ce qu'une autre règle
    // reprenne la main : coller les lignes est le comportement du Markdown.
    const body: string[] = [];
    while (i < lines.length) {
      const current = lines[i] ?? '';
      if (
        current.trim() === '' ||
        HEADING.test(current) ||
        QUOTE.test(current) ||
        ITEM.test(current) ||
        RULE.test(current.trim()) ||
        FENCE.test(current.trim())
      ) {
        break;
      }
      body.push(current);
      i += 1;
    }
    blocks.push({ kind: 'para', text: body.join('\n') });
  }

  return blocks;
}

/**
 * **Une liste, et ce qui pend dessous.**
 *
 * Le retrait décide seul : plus creusé que la ligne d'ouverture, c'est une
 * sous-liste rattachée à l'item précédent ; moins creusé, la liste est finie et
 * la ligne appartient à celle du dessus. Un changement de nature au même retrait
 * — une puce après un numéro — **ferme** la liste plutôt que de la mélanger, ce
 * qui est ce que fait tout lecteur de Markdown.
 */
function parseList(lines: readonly string[], start: number): { block: Block; next: number } {
  const first = ITEM.exec(lines[start] ?? '');
  const indent = (first?.[1] ?? '').length;
  const ordered = first?.[2] === undefined;
  const items: ListItem[] = [];
  let i = start;

  while (i < lines.length) {
    const match = ITEM.exec(lines[i] ?? '');
    if (!match) break;
    const width = (match[1] ?? '').length;
    if (width < indent) break;

    if (width > indent) {
      // Une sous-liste sans ligne au-dessus n'a rien à quoi se rattacher : on la
      // laisse à l'appelant plutôt que d'inventer un item vide.
      const last = items[items.length - 1];
      if (last === undefined) break;
      const nested = parseList(lines, i);
      items[items.length - 1] = { ...last, children: [...(last.children ?? []), nested.block] };
      i = nested.next;
      continue;
    }

    if ((match[2] === undefined) !== ordered) break;
    let text = match[3] ?? '';
    i += 1;

    // **La suite d'une puce tient sur plusieurs lignes** *(2026-09-20)*.
    //
    // Un texte écrit à la main est replié autour de cent colonnes : la deuxième
    // ligne d'une puce ne commence donc par aucun tiret. Sans ce rattrapage, la
    // liste s'arrêtait à la première ligne repliée et le reste de la phrase
    // repartait **en paragraphe**, à la marge, sous sa propre puce — c'est ce
    // qu'on voyait dans « Nouveautés ».
    //
    // C'est la règle du Markdown (*lazy continuation*) : une ligne qui n'ouvre
    // aucun autre bloc appartient à la puce en cours. Elle est recollée par une
    // **espace**, comme un paragraphe replié.
    while (i < lines.length) {
      const next = lines[i] ?? '';
      if (
        next.trim() === '' ||
        ITEM.test(next) ||
        HEADING.test(next) ||
        QUOTE.test(next) ||
        RULE.test(next.trim()) ||
        FENCE.test(next.trim())
      ) {
        break;
      }
      text = `${text} ${next.trim()}`;
      i += 1;
    }

    const todo = TODO.exec(text);
    items.push(
      todo ? { text: todo[2] ?? '', checked: (todo[1] ?? '').toLowerCase() === 'x' } : { text },
    );
  }

  return { block: { kind: 'list', ordered, items }, next: i };
}

// ── Le niveau ligne courante ─────────────────────────────────────────────────

/**
 * Découpe le texte en ligne. Le **code en ligne d'abord** : à l'intérieur, ni
 * gras ni lien ne jouent, et l'inverse ferait que `` `**x**` `` s'afficherait en
 * gras alors qu'il montre littéralement des astérisques.
 */
const INLINE =
  /(\\[!-/:-@[-`{-~])|(`[^`]+`)|(!\[[^\]]*\]\([^)]*\))|(\[[^\]]*\]\([^)]*\))|(\*\*\*[^*]+\*\*\*)|(\*\*[^*]+\*\*)|(~~[^~]+~~)|(\*[^*]+\*)|(_[^_]+_)/;

/**
 * **La grammaire en ligne, une fois pour toutes.**
 *
 * Elle sert deux rendus : les nœuds Preact du lecteur, et les nœuds DOM de
 * l'éditeur WYSIWYG. Écrire la seconde à part aurait produit une deuxième
 * grammaire — c'est-à-dire deux lectures possibles du même texte, et un
 * aller-retour dans l'éditeur qui change des choses que le lecteur affichait
 * autrement. C'est exactement le défaut que le codec de l'app a dû réparer côté
 * mobile ; on ne le refait pas ici.
 */
export type InlineToken =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'code' | 'strong' | 'em' | 'strongEm' | 'strike'; readonly text: string }
  | { readonly kind: 'link' | 'image'; readonly label: string; readonly href: string };

export function inlineTokens(source: string): InlineToken[] {
  const out: InlineToken[] = [];
  let rest = source;

  /** Deux textes voisins sont **un** texte : un `\[` en coupe un en trois. */
  const push = (token: InlineToken): void => {
    const last = out[out.length - 1];
    if (token.kind === 'text' && last?.kind === 'text') {
      out[out.length - 1] = { kind: 'text', text: last.text + token.text };
      return;
    }
    out.push(token);
  };

  while (rest.length > 0) {
    const match = INLINE.exec(rest);
    if (!match || match.index === undefined) {
      push({ kind: 'text', text: rest });
      break;
    }
    if (match.index > 0) push({ kind: 'text', text: rest.slice(0, match.index) });
    const token = match[0];

    if (token.startsWith('\\')) {
      // **L'échappement, appris avec TipTap** *(2026-08-17)*. Le sérialiseur de
      // l'éditeur protège la ponctuation qui serait relue comme de la syntaxe —
      // c'est ce qui empêche un `[clique](…)` littéral de redevenir un lien à la
      // relecture. Sans cette règle, le lecteur affichait la barre oblique, et
      // les deux grammaires ne disaient plus la même chose du même fichier.
      push({ kind: 'text', text: token.slice(1) });
    } else if (token.startsWith('`')) {
      out.push({ kind: 'code', text: token.slice(1, -1) });
    } else if (token.startsWith('![') || token.startsWith('[')) {
      const body = token.startsWith('![') ? token.slice(1) : token;
      out.push({
        kind: token.startsWith('![') ? 'image' : 'link',
        label: body.slice(1, body.indexOf(']')),
        href: body.slice(body.indexOf('](') + 2, -1),
      });
    } else if (token.startsWith('***')) {
      out.push({ kind: 'strongEm', text: token.slice(3, -3) });
    } else if (token.startsWith('**')) {
      out.push({ kind: 'strong', text: token.slice(2, -2) });
    } else if (token.startsWith('~~')) {
      out.push({ kind: 'strike', text: token.slice(2, -2) });
    } else {
      out.push({ kind: 'em', text: token.slice(1, -1) });
    }

    rest = rest.slice(match.index + token.length);
  }

  return out;
}

/**
 * **Le mode inerte** — le même rendu, sans un seul descendant interactif.
 *
 * Il existe pour un endroit précis : l'extrait vit **dans le bouton** d'une tuile.
 * Un `<a>` ou une case à cocher à l'intérieur d'un bouton est du contenu
 * interactif imbriqué — le clic devient ambigu, le parcours au clavier s'arrête
 * dans une cible qui n'ouvre rien, et le HTML est invalide. Le lien garde donc son
 * libellé et perd son ancre ; la case devient son glyphe.
 *
 * Ce n'est pas une seconde grammaire : c'est **la même**, à laquelle on retire ce
 * qui ne peut pas vivre là. Une grammaire allégée finirait par diverger, et la
 * tuile mentirait sur ce qu'on trouve en ouvrant.
 */
export interface RenderOptions {
  readonly inert?: boolean;
}

export function renderInline(
  text: string,
  keyBase = '',
  options: RenderOptions = {},
): ComponentChildren[] {
  return inlineTokens(text).map((token, n) => {
    const key = `${keyBase}i${n}`;
    switch (token.kind) {
      case 'text':
        return breakLines(token.text, key);
      case 'code':
        return <code key={key}>{token.text}</code>;
      case 'image':
        // Une image d'extrait se résoudrait en URL présignée, **par tuile
        // montée** : c'est la N+1 que l'attribut projeté supprime. Le serveur la
        // réduit déjà à son texte ; ceci tient pour les extraits écrits avant
        // 4.5 bis.
        return options.inert ? (
          <span key={key}>{token.label}</span>
        ) : (
          <NoteImage key={key} src={token.href} alt={token.label} />
        );
      case 'link':
        return options.inert ? (
          <span key={key} class="md-link-inert">
            {token.label || token.href}
          </span>
        ) : (
          <NoteLink key={key} href={token.href} label={token.label} />
        );
      case 'strongEm':
        return (
          <strong key={key}>
            <em>{token.text}</em>
          </strong>
        );
      case 'strong':
        return <strong key={key}>{token.text}</strong>;
      case 'strike':
        return <s key={key}>{token.text}</s>;
      case 'em':
        return <em key={key}>{token.text}</em>;
    }
  });
}

/**
 * **Un retour à la ligne se voit** *(2026-08-17)*.
 *
 * Le Markdown colle les lignes d'un paragraphe, et le lecteur les rendait en un
 * seul nœud de texte : `white-space: normal` réduisait le `\n` à une espace, si
 * bien qu'une note écrite sur deux lignes s'affichait sur une. L'éditeur, lui,
 * les a toujours montrées séparées — c'était donc un **désaccord de grammaire**,
 * pas une préférence de mise en page, et il ne se voyait qu'en comparant les deux
 * surfaces.
 */
function breakLines(text: string, key: string): ComponentChildren[] {
  const lines = text.split('\n');
  if (lines.length === 1) return [text];
  return lines.flatMap((line, index) =>
    index === 0 ? [line] : [<br key={`${key}b${index}`} />, line],
  );
}

/**
 * Un lien. **Seuls `http(s)` et `mailto` sortent en `<a>`** : un `javascript:`
 * ne s'exécuterait pas sous cette CSP, mais s'en remettre à elle serait accepter
 * qu'une note reçue puisse porter une amorce. Le reste s'affiche en texte.
 */
function NoteLink({ href, label }: { readonly href: string; readonly label: string }): JSX.Element {
  const safe = /^(https?:|mailto:)/i.test(href.trim());
  if (!safe) return <span>{label || href}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid="md-link">
      {label || href}
    </a>
  );
}

/**
 * Une image embarquée. La forme canonique est **`synkara://media/<fid>`** et non
 * un chemin : une note est lue depuis un autre appareil, où aucun chemin local
 * n'existe. Le `fid` se résout en URL présignée à l'affichage, sur le rôle
 * `standard` — la source d'un appareil photo pèse dix mégaoctets.
 *
 * Elle passe par `noteImageUrl` et non par `urlFor` **depuis N2** : une photo
 * insérée dans la note qu'on vient d'écrire n'a pas encore son dérivé, et le
 * refus `bytes_not_ready` du lot se lisait ici comme un média supprimé. C'est le
 * même appel que l'éditeur, pour que les deux surfaces attendent pareil.
 */
function NoteImage({ src, alt }: { readonly src: string; readonly alt: string }): JSX.Element {
  const fid = /^synkara:\/\/media\/(\w+)$/.exec(src.trim())?.[1];
  const [url, setUrl] = useState<string | undefined>(fid ? undefined : src);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!fid) return;
    let alive = true;
    void noteImageUrl(fid)
      .then((signed) => alive && setUrl(signed))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [fid]);

  // Une image dont le média a été supprimé ne doit pas laisser un rectangle
  // cassé : son texte de remplacement **est** l'information restante.
  if (failed) return <span class="md-image-missing">{alt || fid}</span>;
  if (!url) return <span class="md-image-loading" aria-hidden="true" />;
  if (!/^(https?:|data:)/i.test(url)) return <span>{alt}</span>;
  return <img class="md-image" src={url} alt={alt} data-testid="md-image" loading="lazy" />;
}

// ── L'assemblage ─────────────────────────────────────────────────────────────

/** Rend une note. `data-testid="markdown"` est la racine du rendu. */
export function Markdown({ source }: { readonly source: string }): JSX.Element {
  const blocks = parseBlocks(source);
  return (
    <div class="markdown" data-testid="markdown">
      {blocks.map((block, index) => renderBlock(block, `b${index}`))}
    </div>
  );
}

/**
 * **L'extrait d'une note, dans une tuile** — le même moteur, à une autre échelle.
 *
 * Jusqu'à 4.5 bis, la tuile posait l'extrait en texte : le serveur l'avait aplati,
 * il n'y avait donc plus rien à interpréter, et une liste de courses se peignait
 * comme un paragraphe gris. Le serveur garde désormais le Markdown ; ce qui rend
 * ici est **exactement** ce qui rend dans le lecteur — un titre reste un titre, une
 * case à cocher reste une case.
 *
 * Rien n'est réécrit pour l'occasion : une seconde grammaire « allégée » finirait
 * par diverger de la première, et la tuile mentirait sur ce qu'on trouvera en
 * ouvrant. Ce qui change est **la taille**, et elle vit dans la CSS.
 */
export function MarkdownExcerpt({
  source,
  testId,
}: {
  readonly source: string;
  readonly testId: string;
}): JSX.Element {
  const blocks = parseBlocks(source);
  return (
    <div class="markdown md-excerpt" data-testid={testId} aria-hidden="true">
      {blocks.map((block, index) => renderBlock(block, `x${index}`, { inert: true }))}
    </div>
  );
}

function renderBlock(block: Block, key: string, options: RenderOptions = {}): JSX.Element {
  switch (block.kind) {
    case 'heading': {
      // Les niveaux sont bornés à 6, et le titre reste un titre : le style vient
      // du CSS, jamais d'un `<div>` grossi.
      const Tag = `h${Math.min(block.level, 6)}` as 'h1';
      return <Tag key={key}>{renderInline(block.text, key, options)}</Tag>;
    }
    case 'para':
      return <p key={key}>{renderInline(block.text, key, options)}</p>;
    case 'code':
      return (
        <pre key={key} class="md-code" data-language={block.language || undefined}>
          <code>{block.text}</code>
        </pre>
      );
    case 'quote':
      return <blockquote key={key}>{renderInline(block.text, key, options)}</blockquote>;
    case 'rule':
      return <hr key={key} />;
    case 'list': {
      const Tag = (block.ordered ? 'ol' : 'ul') as 'ul';
      return (
        <Tag key={key} class={block.items.some((it) => it.checked !== undefined) ? 'md-todo' : undefined}>
          {block.items.map((item, index) => (
            <li key={`${key}l${index}`}>
              {item.checked === undefined ? null : options.inert ? (
                // Le glyphe plutôt que la case : dans un bouton de tuile, une case
                // serait un descendant interactif — donc un second point de clic
                // dans une cible qui n'en a qu'un.
                //
                // C'est une **icône du jeu** et non `☑`/`☐` : les deux caractères
                // se dessinaient dans la police disponible, donc autrement d'une
                // machine à l'autre, et manquaient purement et simplement sur
                // certaines — l'aperçu d'une liste de courses y devenait une
                // colonne de rectangles vides.
                <span
                  class={item.checked ? 'md-todo-glyph done' : 'md-todo-glyph'}
                  aria-hidden="true"
                >
                  <IconCheckbox size={14} checked={item.checked} />
                </span>
              ) : (
                // Une case **en lecture seule** : ce rendu n'édite pas, et une
                // case cliquable qui n'enregistre rien serait un mensonge.
                <input
                  type="checkbox"
                  checked={item.checked}
                  disabled
                  aria-label={item.text}
                  data-testid="md-todo"
                />
              )}
              {renderInline(item.text, `${key}l${index}`, options)}
              {/* Ce qui pend sous la ligne — une sous-liste, rendue par le même
                  chemin : une seconde règle « allégée » finirait par diverger. */}
              {item.children?.map((child, sub) =>
                renderBlock(child, `${key}l${index}s${sub}`, options),
              )}
            </li>
          ))}
        </Tag>
      );
    }
    case 'table':
      return (
        <div key={key} class="md-table-wrap">
          <table class="md-table">
            <thead>
              <tr>
                {block.head.map((cell, index) => (
                  <th key={`${key}h${index}`}>{renderInline(cell, `${key}h${index}`, options)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={`${key}r${r}`}>
                  {row.map((cell, c) => (
                    <td key={`${key}r${r}c${c}`}>
                      {renderInline(cell, `${key}r${r}c${c}`, options)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}
